import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, HTMLAttributes, ReactNode, RefObject } from "react";

export type PopoverPlacement = "bottom-start" | "bottom-end" | "top-start" | "top-end" | "right-start" | "left-start";

export type PopoverProps = Omit<HTMLAttributes<HTMLDivElement>, "role"> & {
  open: boolean;
  onClose: () => void;
  anchorRef?: RefObject<HTMLElement | null>;
  placement?: PopoverPlacement;
  role?: "dialog" | "menu" | "listbox" | "group";
  label?: string;
  autoFocus?: boolean;
  dataFxmap?: string;
  children: ReactNode;
};

type Rect = Pick<DOMRect, "top" | "left" | "right" | "bottom">;

export const popoverGap = 8;

export function focusablesOf(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])")].filter(a => !a.hasAttribute("disabled") && !a.hidden);
}

export function popoverPosition(placement: PopoverPlacement, anchor: Rect, host: Rect, gap = popoverGap): CSSProperties {
  const left = anchor.left - host.left;
  const right = host.right - anchor.right;
  const below = anchor.bottom - host.top + gap;
  const above = host.bottom - anchor.top + gap;
  const top = anchor.top - host.top;

  switch (placement) {
    case "bottom-start":
      return { top: below, left };
    case "bottom-end":
      return { top: below, right };
    case "top-start":
      return { bottom: above, left };
    case "top-end":
      return { bottom: above, right };
    case "right-start":
      return { top, left: anchor.right - host.left + gap };
    case "left-start":
      return { top, right: host.right - anchor.left + gap };
  }
}

type Placed = { host: HTMLElement; style: CSSProperties };

export function Popover({ open, onClose, anchorRef, placement = "bottom-start", role = "dialog", label, autoFocus = true, dataFxmap, className, style, children, ...rest }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const [placed, setPlaced] = useState<Placed | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPlaced(null);
      return;
    }

    const anchor = anchorRef?.current ?? null;
    const host = anchor?.closest<HTMLElement>(".fxmap") ?? null;

    if (anchor === null || host === null) {
      setPlaced(null);
      return;
    }

    const measure = () => setPlaced({ host, style: popoverPosition(placement, anchor.getBoundingClientRect(), host.getBoundingClientRect()) });

    measure();
    window.addEventListener("resize", measure);

    return () => window.removeEventListener("resize", measure);
  }, [open, placement, anchorRef]);

  useEffect(() => {
    if (!open) return;

    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (autoFocus) {
      const element = ref.current;

      if (element !== null) {
        const first = focusablesOf(element)[0];

        (first ?? element).focus();
      }
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;

      if (target === null) return;
      if (ref.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;

      onClose();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      event.stopPropagation();
      onClose();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);

      const restore = restoreRef.current;

      if (restore !== null && document.contains(restore)) restore.focus();
    };
  }, [open, onClose, anchorRef, autoFocus, placed]);

  if (!open) return null;

  const node = (
    <div
      {...rest}
      ref={ref}
      className={["fxmap-popover", "fxmap-card", placed === null ? "" : "fxmap-popover-placed", className ?? ""].filter(Boolean).join(" ")}
      role={role}
      aria-label={label}
      data-placement={placement}
      data-fxmap={dataFxmap}
      tabIndex={-1}
      style={placed === null ? style : { ...style, ...placed.style }}
    >
      {children}
    </div>
  );

  return placed === null ? node : createPortal(node, placed.host);
}
